import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@trillion/ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@trillion/ui/components/ui/card";
import { Input } from "@trillion/ui/components/ui/input";
import { Label } from "@trillion/ui/components/ui/label";
import { Badge } from "@trillion/ui/components/ui/badge";
import { Separator } from "@trillion/ui/components/ui/separator";
import { Avatar, AvatarFallback } from "@trillion/ui/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@trillion/ui/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@trillion/ui/components/ui/alert-dialog";
import { ApiError } from "@trillion/ui/lib/api-client";
import { toast } from "sonner";
import { InlineEditText } from "../components/inline-edit-text";
import { api } from "../lib/api";
import type { Team, TeamInvite, TeamMember } from "../types/board";
import { TeamRole } from "../types/enums";

// Enum-keyed so a new member is a compile error; a numeric enum interpolates as its integer, so the
// badge must render the label rather than {member.team_role_id}.
const teamRoleLabels: Record<TeamRole, string> = {
  [TeamRole.TeamAdmin]: "Admin",
  [TeamRole.TeamMember]: "Member",
};

// current_user_id is the caller's Board user id (what team_members.user_id holds) and can_manage is the
// API's own admin rule — /auth/me's id is the Passport id for SSO users, so it can never be compared here.
interface TeamDetail extends Team {
  members: TeamMember[];
  current_user_id: string;
  can_manage: boolean;
}

interface InviteResponse {
  added?: boolean;
  invited?: boolean;
  email: string;
}

// ApiError.body is the raw response text; the API's refusals are {"error": "..."}.
function apiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.status === 409) {
    try {
      const parsed: unknown = JSON.parse(err.body);
      if (parsed && typeof parsed === "object" && "error" in parsed && typeof parsed.error === "string") {
        return parsed.error;
      }
    } catch {
      // body was not JSON — fall through to the generic message
    }
  }
  return fallback;
}

export function TeamDetailPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const [team, setTeam] = useState<TeamDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<TeamRole>(TeamRole.TeamMember);
  const [isInviting, setIsInviting] = useState(false);
  const [invites, setInvites] = useState<TeamInvite[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const isAdmin = team?.can_manage === true;

  useEffect(() => {
    if (!teamId) return;
    loadTeam();
  }, [teamId]);

  useEffect(() => {
    if (!isAdmin) return;
    loadInvites();
  }, [teamId, isAdmin]);

  function loadTeam() {
    if (!teamId) return;
    api<{ team: Team; members: TeamMember[]; current_user_id: string; can_manage: boolean }>(
      `/api/teams/${teamId}`
    )
      .then((res) =>
        setTeam({
          ...res.team,
          members: res.members,
          current_user_id: res.current_user_id,
          can_manage: res.can_manage,
        })
      )
      .catch(() => setError("Failed to load team"))
      .finally(() => setIsLoading(false));
  }

  function loadInvites() {
    if (!teamId) return;
    api<{ invites: TeamInvite[] }>(`/api/teams/${teamId}/invites`)
      .then((res) => setInvites(res.invites))
      .catch(() => toast.error("Failed to load pending invites"));
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!teamId || !inviteEmail.trim()) return;
    setIsInviting(true);
    try {
      const res = await api<InviteResponse>(`/api/teams/${teamId}/invite`, {
        method: "POST",
        body: JSON.stringify({ email: inviteEmail.trim(), team_role_id: inviteRole }),
      });
      setInviteEmail("");
      setInviteRole(TeamRole.TeamMember);
      if (res.added) {
        toast.success("Member added");
        loadTeam();
        // Adding an existing user may have accepted their pending invite — drop it from the list
        loadInvites();
      } else {
        toast.success("Invite saved — they'll join on their first Board sign-in");
        loadInvites();
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to send invite"));
    } finally {
      setIsInviting(false);
    }
  }

  async function saveTeamField(field: "name" | "description", value: string): Promise<boolean> {
    if (!teamId) return false;
    try {
      const updated = await api<Team>(`/api/teams/${teamId}`, {
        method: "PUT",
        body: JSON.stringify({ [field]: value }),
      });
      // Merge only the saved field, so a late response can never overwrite a newer value of the other.
      setTeam((prev) => (prev ? { ...prev, [field]: updated[field] } : prev));
      toast.success("Team updated");
      return true;
    } catch {
      toast.error("Failed to update team");
      return false;
    }
  }

  async function handleRemoveMember(userId: string) {
    if (!teamId) return;
    try {
      await api(`/api/teams/${teamId}/members/${encodeURIComponent(userId)}`, { method: "DELETE" });
      toast.success("Member removed");
      loadTeam();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to remove member"));
    }
  }

  async function handleRoleChange(userId: string, teamRoleId: TeamRole) {
    if (!teamId) return;
    try {
      await api(`/api/teams/${teamId}/members/${encodeURIComponent(userId)}/role`, {
        method: "PUT",
        body: JSON.stringify({ team_role_id: teamRoleId }),
      });
      toast.success("Role updated");
      loadTeam();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to update role"));
      // Snap the role Select back to the persisted role.
      loadTeam();
    }
  }

  async function handleCancelInvite(inviteId: string) {
    if (!teamId) return;
    try {
      await api(`/api/teams/${teamId}/invites/${encodeURIComponent(inviteId)}`, { method: "DELETE" });
      toast.success("Invite cancelled");
    } catch {
      toast.error("Failed to cancel invite");
    }
    loadInvites();
  }

  async function handleDeleteTeam() {
    if (!teamId) return;
    setIsDeleting(true);
    try {
      await api(`/api/teams/${teamId}`, { method: "DELETE" });
      toast.success("Team deleted");
      navigate("/teams");
    } catch {
      toast.error("Failed to delete team");
      setIsDeleting(false);
    }
  }

  function getInitials(name?: string, email?: string): string {
    const value = name || email || "?";
    return value
      .split(/[\s@]+/)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase() ?? "")
      .join("");
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading team...</p>
      </div>
    );
  }

  if (error || !team) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <p className="text-sm text-destructive">{error ?? "Team not found"}</p>
        <Button asChild variant="outline" size="sm">
          <Link to="/teams">Back to Teams</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl p-6">
      <div className="mb-2">
        <Button asChild variant="ghost" size="sm" className="mb-2">
          <Link to="/teams">Back to Teams</Link>
        </Button>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-semibold">
          <InlineEditText
            value={team.name}
            label="Team name"
            editable={isAdmin}
            required
            maxLength={200}
            onSave={(next) => saveTeamField("name", next)}
          />
        </h1>
        <div className="mt-1 text-sm text-muted-foreground">
          <InlineEditText
            value={team.description ?? ""}
            label="Team description"
            editable={isAdmin}
            multiline
            placeholder="Add a description"
            onSave={(next) => saveTeamField("description", next)}
          />
        </div>
      </div>

      {isAdmin && (
        <>
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-base">Invite Member</CardTitle>
              <CardDescription>
                Invite a user by email to join this team.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleInvite} className="flex items-end gap-3">
                <div className="flex-1 space-y-2">
                  <Label htmlFor="invite-email">Email address</Label>
                  <Input
                    id="invite-email"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="colleague@company.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invite-role">Invite role</Label>
                  <Select
                    value={String(inviteRole)}
                    onValueChange={(value) => setInviteRole(Number(value) as TeamRole)}
                  >
                    <SelectTrigger id="invite-role" className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(TeamRole)
                        .filter((r): r is TeamRole => typeof r === "number")
                        .map((role) => (
                          <SelectItem key={role} value={String(role)}>
                            {teamRoleLabels[role]}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" disabled={isInviting}>
                  {isInviting ? "Inviting..." : "Invite"}
                </Button>
              </form>
            </CardContent>
          </Card>
          {invites.length > 0 && (
            <div className="mb-6">
              <h2 className="mb-4 text-lg font-semibold">Pending invites ({invites.length})</h2>
              <div className="space-y-3">
                {invites.map((invite) => (
                  <div
                    key={invite.invite_id}
                    className="flex items-center justify-between rounded-lg border p-3"
                  >
                    <div>
                      <p className="text-sm font-medium">{invite.email}</p>
                      <p className="text-xs text-muted-foreground">
                        {teamRoleLabels[invite.team_role_id]} · invited{" "}
                        {new Date(invite.created_date).toLocaleDateString()}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleCancelInvite(invite.invite_id)}
                    >
                      Cancel
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Separator className="mb-6" />
        </>
      )}

      <div>
        <h2 className="mb-4 text-lg font-semibold">
          Members ({team.members.length})
        </h2>
        <div className="space-y-3">
          {team.members.map((member) => (
            <div
              key={member.team_member_id}
              className="flex items-center justify-between rounded-lg border p-3"
            >
              <div className="flex items-center gap-3">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="text-xs">
                    {getInitials(member.display_name, member.email)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-medium">
                    {member.display_name || member.email || member.user_id}
                  </p>
                  {member.email && member.display_name && (
                    <p className="text-xs text-muted-foreground">{member.email}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && member.user_id !== team.current_user_id ? (
                  <>
                    <Select
                      value={String(member.team_role_id)}
                      onValueChange={(value) =>
                        handleRoleChange(member.user_id, Number(value) as TeamRole)
                      }
                    >
                      <SelectTrigger className="h-8 w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={String(TeamRole.TeamAdmin)}>
                          {teamRoleLabels[TeamRole.TeamAdmin]}
                        </SelectItem>
                        <SelectItem value={String(TeamRole.TeamMember)}>
                          {teamRoleLabels[TeamRole.TeamMember]}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleRemoveMember(member.user_id)}
                    >
                      Remove
                    </Button>
                  </>
                ) : (
                  <Badge
                    variant={member.team_role_id === TeamRole.TeamAdmin ? "default" : "secondary"}
                  >
                    {teamRoleLabels[member.team_role_id]}
                  </Badge>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {isAdmin && (
        <>
          <Separator className="my-6" />
          <Card className="border-destructive/50">
            <CardHeader>
              <CardTitle className="text-base">Delete team</CardTitle>
              <CardDescription>
                Permanently deletes this team, its members and its pending invites, and removes its diagram
                shares. Diagrams are never deleted.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" disabled={isDeleting}>
                    Delete team
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete {team.name}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This cannot be undone. Diagrams stay; the team's shares are removed. Members keep access
                      to diagrams they own, and to others only through another share.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteTeam}>Delete team</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
